import React from 'react';
import JsonModal from './JsonModal';
import { formatRuDate, formatRuDateTime } from '../../utils/ruDateUtils';
import './SessionsTable.scss';

const SessionsTable = ({ 
    sessions, 
    loading, 
    error, 
    onRetry, 
    columns, 
    isGroupStats = false
}) => {
    const [jsonModal, setJsonModal] = React.useState({
        isOpen: false,
        data: null,
        title: ''
    });
    const [sortConfig, setSortConfig] = React.useState({
        key: null,
        direction: 'asc'
    });

    const formatDate = (dateString) => {
        const date = new Date(dateString);
        return formatRuDateTime(date);
    };

    const getStatusBadge = (status) => {
        const statusMap = {
            2: { label: 'Pending', color: 'bg-yellow-200' },
            1: { label: 'Completed', color: 'bg-green-200' },
            '-1': { label: 'Failed', color: 'bg-red-200' },
            0: { label: 'Running', color: 'bg-blue-200' }
        };
        const statusInfo = statusMap[status] || { label: 'Unknown', color: 'bg-gray-200' };
        return <span className={`status-badge ${statusInfo.color}`}>{statusInfo.label}</span>;
    };

    const openJsonModal = (data, title) => setJsonModal({ isOpen: true, data, title });
    const closeJsonModal = () => setJsonModal({ isOpen: false, data: null, title: '' });

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') direction = 'desc';
        setSortConfig({ key, direction });
    };

    const sortData = (data) => {
        if (!sortConfig.key) return data;
        return [...data].sort((a, b) => {
            let aValue = a[sortConfig.key] ?? '';
            let bValue = b[sortConfig.key] ?? '';

            if (['created_at','start_date','end_date'].includes(sortConfig.key)) {
                aValue = aValue ? new Date(aValue).getTime() : 0;
                bValue = bValue ? new Date(bValue).getTime() : 0;
            }
            if (['total','completed','failed','running','pending'].includes(sortConfig.key)) {
                aValue = Number(aValue) || 0;
                bValue = Number(bValue) || 0;
            }
            if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1;
            if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1;
            return 0;
        });
    };

    const getSortIcon = (column) => {
        if (sortConfig.key !== column) return <i className="fas fa-sort text-gray-400"></i>;
        return sortConfig.direction === 'asc' 
            ? <i className="fas fa-sort-up text-blue-500"></i>
            : <i className="fas fa-sort-down text-blue-500"></i>;
    };

    if (loading) return (
        <div className="p-8 flex justify-center items-center">
            <div className="loading-spinner text-blue-500">
                <i className="fas fa-circle-notch fa-2x"></i>
            </div>
            <span className="ml-3 text-gray-600">Loading sessions...</span>
        </div>
    );

    if (error) return (
        <div className="p-8 flex items-center justify-center text-red-500">
            <div className="text-center">
                <i className="fas fa-exclamation-triangle mr-2"></i>
                {error}
                <button onClick={onRetry} className="ml-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600">
                    Retry
                </button>
            </div>
        </div>
    );

    if (!sessions || sessions.length === 0) return (
        <div className="p-8 flex items-center justify-center text-gray-500">
            <div className="text-center">
                <i className="fas fa-inbox fa-2x mb-2"></i>
                <p>Нет данных для отображения</p>
            </div>
        </div>
    );

    return (
        <>
            <div className="sessions-table-container">
                <table className="sessions-table">
                    <thead>
                        <tr>
                            {columns.map(column => (
                                <th key={column} onClick={() => handleSort(column)}>
                                    <div className="flex items-center justify-between">
                                        <span>{column.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase())}</span>
                                        {getSortIcon(column)}
                                    </div>
                                </th>
                            ))}
                            {!isGroupStats && <th>Actions</th>}
                        </tr>
                    </thead>
                </table>
                <div className="max-h-96 overflow-y-auto">
                    <table className="sessions-table">
                        <tbody>
                            {sortData(sessions).map((session, index) => (
                                <tr key={session.session_id || index}>
                                    {columns.map(column => (
                                        <td key={column}>
                                            {column === 'status' ? getStatusBadge(session[column]) :
                                             column === 'created_at' ? formatDate(session[column]) :
                                             column === 'start_date' || column === 'end_date' ? 
                                                (session[column] ? formatRuDate(session[column]) : '-') :
                                             session[column] || '-'}
                                        </td>
                                    ))}
                                    {!isGroupStats && (
                                        <td>
                                            <div className="flex items-center space-x-2">
                                                {session.details && (
                                                    <button 
                                                        onClick={() => openJsonModal(session.details, "Session Details")}
                                                        className="table-action-btn text-blue-600"
                                                        title="View Details"
                                                    >
                                                        <i className="fas fa-file-alt"></i>
                                                    </button>
                                                )}
                                                {session.params && (
                                                    <button 
                                                        onClick={() => openJsonModal(session.params, "Session Parameters")}
                                                        className="table-action-btn text-green-600"
                                                        title="View Parameters"
                                                    >
                                                        <i className="fas fa-cogs"></i>
                                                    </button>
                                                )}
                                            </div>
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            <JsonModal
                isOpen={jsonModal.isOpen}
                onClose={closeJsonModal}
                data={jsonModal.data}
                title={jsonModal.title}
            />
        </>
    );
};

export default SessionsTable;
